/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ChatParticipant,
  ChatParticipant$inboundSchema,
} from "./chatparticipant.js";

export type ChatMessage = {
  /**
   * The content of the message — string for plain text, array for multimodal/tool-call content parts, null for assistant messages that only carry tool_calls
   */
  content?: any | undefined;
  /**
   * When the message was created.
   */
  createdAt: Date;
  /**
   * The ID of the external user who created the message
   */
  externalUserId?: string | undefined;
  /**
   * The finish reason of the message
   */
  finishReason?: string | undefined;
  /**
   * Conversation generation — bumps on compaction or edit divergence
   */
  generation: number;
  /**
   * The ID of the message
   */
  id: string;
  /**
   * Present only in `risk_only` mode: true when this message has an active risk finding, false for the surrounding-context messages padded around it.
   */
  isRisk?: boolean | undefined;
  /**
   * The model that generated the message
   */
  model: string;
  /**
   * Observed per-message conversation participants, independent of message ownership.
   */
  participants?: Array<ChatParticipant> | undefined;
  /**
   * The agent prompt/turn ID associated with this message, when available.
   */
  promptId?: string | undefined;
  /**
   * The role of the message
   */
  role: string;
  /**
   * Monotonic sequence number of the message. Strictly increasing within a chat; use it as the keyset cursor for `before_seq`/`after_seq` pagination. Not contiguous (the sequence is shared across chats), so do not infer gaps from arithmetic differences.
   */
  seq: number;
  /**
   * The tool call ID of the message
   */
  toolCallId?: string | undefined;
  /**
   * The tool calls in the message as a JSON blob
   */
  toolCalls?: string | undefined;
  /**
   * The ID of the user who created the message
   */
  userId?: string | undefined;
};

/** @internal */
export const ChatMessage$inboundSchema: z.ZodMiniType<ChatMessage, unknown> = z
  .pipe(
    z.object({
      content: z.optional(z.any()),
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      external_user_id: z.optional(z.string()),
      finish_reason: z.optional(z.string()),
      generation: z.int(),
      id: z.string(),
      is_risk: z.optional(z.boolean()),
      model: z.string(),
      participants: z.optional(z.array(ChatParticipant$inboundSchema)),
      prompt_id: z.optional(z.string()),
      role: z.string(),
      seq: z.int(),
      tool_call_id: z.optional(z.string()),
      tool_calls: z.optional(z.string()),
      user_id: z.optional(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "external_user_id": "externalUserId",
        "finish_reason": "finishReason",
        "is_risk": "isRisk",
        "prompt_id": "promptId",
        "tool_call_id": "toolCallId",
        "tool_calls": "toolCalls",
        "user_id": "userId",
      });
    }),
  );

export function chatMessageFromJSON(
  jsonString: string,
): SafeParseResult<ChatMessage, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ChatMessage$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ChatMessage' from JSON`,
  );
}
